import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loading } from '@vnetwork/ui/feedback';
import { useAuth } from '@/auth/AuthProvider';
import { exchangeSsoCode } from '@/api/auth-flow';
import {
  SSO_INTENDED_KEY,
  SSO_VERIFIER_KEY,
  ssoRedirectUri,
  ssoSessionFromTokens,
} from '@/lib/sso';
import { AuthShell } from './components/AuthShell';
import { SsoMfaVerificationForm } from './components/SsoMfaVerificationForm';

function failTo(navigate: ReturnType<typeof useNavigate>, message: string) {
  const qs = new URLSearchParams({ error: 'callback_failed', message });
  navigate(`/auth/login?${qs.toString()}`, { replace: true });
}

type Step = { kind: 'exchanging' } | { kind: 'mfa'; sessionId: string };

/**
 * SSO return URL. Exchanges the authorization `code` (with the stored PKCE
 * verifier) for app tokens via the backend, then starts the session. Accounts
 * with MFA enabled come back with a held `session_id` instead of tokens — those
 * are routed to the second-factor form, which verifies and releases the tokens.
 */
export default function CallbackPage() {
  const navigate = useNavigate();
  const { setSession } = useAuth();
  // StrictMode mounts effects twice in dev; the code is single-use, so guard.
  const ran = useRef(false);
  // Captured before any navigation/MFA hop so the second factor lands the user
  // on the originally requested URL.
  const intended = useRef('/');
  const [step, setStep] = useState<Step>({ kind: 'exchanging' });

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    const params = new URLSearchParams(window.location.search);
    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const code = params.get('code') ?? hash.get('code');
    const idpError = params.get('error') ?? hash.get('error');
    const verifier = sessionStorage.getItem(SSO_VERIFIER_KEY);
    intended.current = sessionStorage.getItem(SSO_INTENDED_KEY) || '/';

    if (idpError) {
      failTo(navigate, params.get('error_description') ?? idpError);
      return;
    }
    if (!code || !verifier) {
      failTo(navigate, 'Missing authorization code.');
      return;
    }

    (async () => {
      try {
        const result = await exchangeSsoCode({
          code,
          code_verifier: verifier,
          redirect_uri: ssoRedirectUri(),
        });

        if (result?.access_token) {
          const session = ssoSessionFromTokens({
            access_token: result.access_token,
            refresh_token: result.refresh_token,
            exp: result.exp,
          });
          if (setSession(session, 'sso')) {
            sessionStorage.removeItem(SSO_VERIFIER_KEY);
            sessionStorage.removeItem(SSO_INTENDED_KEY);
            navigate(intended.current, { replace: true });
          } else {
            failTo(navigate, 'Invalid token returned.');
          }
          return;
        }

        // MFA-protected accounts get a held session_id instead of tokens —
        // prompt for the second factor, which verifies and releases them.
        if (result?.session_id) {
          setStep({ kind: 'mfa', sessionId: result.session_id });
          return;
        }

        failTo(navigate, 'No access token returned.');
      } catch (e) {
        failTo(navigate, e instanceof Error ? e.message : 'SSO sign-in failed.');
      }
    })();
  }, [navigate, setSession]);

  if (step.kind === 'mfa') {
    return (
      <AuthShell
        title="Two-factor authentication"
        subtitle="Enter the code from your authenticator app to finish signing in."
      >
        <SsoMfaVerificationForm
          sessionId={step.sessionId}
          intendedUrl={intended.current}
          onBack={() => navigate('/auth/login', { replace: true })}
        />
      </AuthShell>
    );
  }

  return <Loading />;
}
